import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Upload,
  Image as ImageIcon,
  Trash2,
  Copy,
  Check,
  Loader2,
  X,
  RefreshCw,
} from 'lucide-react';
import { mediaAPI, API_BASE } from '../../lib/api';
import { formatDateTime } from '../../lib/utils';
import { toast } from 'sonner';

const AdminMedia = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [reprocessing, setReprocessing] = useState(false);

  useEffect(() => {
    loadMedia();
  }, []);

  const loadMedia = async () => {
    try {
      const response = await mediaAPI.getAll();
      setMedia(response.data);
    } catch (error) {
      toast.error('Erro ao carregar arquivos');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        await mediaAPI.upload(file);
      }
      toast.success(`${files.length} arquivo(s) enviado(s) com sucesso`);
      loadMedia();
    } catch (error) {
      toast.error('Erro ao enviar arquivo(s)');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleCopyUrl = async (item) => {
    const url = `/api/media/${item.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(item.id);
      toast.success('URL copiada');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('Erro ao copiar URL');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await mediaAPI.delete(deleteId);
      toast.success('Arquivo excluído com sucesso');
      loadMedia();
      if (selectedMedia?.id === deleteId) {
        setSelectedMedia(null);
      }
    } catch (error) {
      toast.error('Erro ao excluir arquivo');
    } finally {
      setDeleteId(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleReprocessAll = async () => {
    setReprocessing(true);
    try {
      const response = await mediaAPI.reprocessAll();
      const r = response.data;
      toast.success(`Concluído! ${r.compressed} imagens comprimidas, ${r.saved_mb}MB economizados`);
      loadMedia();
    } catch (error) {
      toast.error('Erro ao reprocessar imagens');
    } finally {
      setReprocessing(false);
    }
  };

  return (
    <div data-testid="admin-media">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Mídia</h1>
          <p className="text-slate-600">Gerencie imagens e arquivos do site</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleReprocessAll}
            disabled={reprocessing}
            data-testid="reprocess-all-btn"
          >
            {reprocessing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Otimizando...</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" />Otimizar todas</>
            )}
          </Button>
          <input
            type="file"
            id="media-upload"
            multiple
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
            data-testid="media-upload-input"
          />
          <label htmlFor="media-upload">
            <Button asChild className="btn-primary cursor-pointer" disabled={uploading}>
              <span>
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Enviar Arquivo
              </span>
            </Button>
          </label>
        </div>
      </div>

      {/* Upload Area */}
      <div className="mb-8">
        <label
          htmlFor="media-upload"
          className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-primary hover:bg-primary/5 transition-all"
        >
          {uploading ? (
            <>
              <Loader2 className="w-8 h-8 text-primary animate-spin mb-2" />
              <span className="text-sm text-slate-500">Enviando...</span>
            </>
          ) : (
            <>
              <Upload className="w-8 h-8 text-slate-400 mb-2" />
              <span className="text-sm text-slate-500">Arraste arquivos aqui ou clique para selecionar</span>
              <span className="text-xs text-slate-400 mt-1">PNG, JPG, GIF, WEBP até 10MB</span>
            </>
          )}
        </label>
      </div>

      {/* Media Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {Array(12).fill(0).map((_, i) => (
            <div key={i} className="skeleton aspect-square rounded-lg" />
          ))}
        </div>
      ) : media.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {media.map((item) => (
            <div
              key={item.id}
              className="group relative aspect-square bg-slate-100 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all"
              onClick={() => setSelectedMedia(item)}
              data-testid={`media-item-${item.id}`}
            >
              {item.content_type?.startsWith('image/') ? (
                <img
                  src={`/api/media/${item.id}`}
                  alt={item.alt_text || item.original_filename}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-12 h-12 text-slate-300" />
                </div>
              )}
              <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyUrl(item);
                  }}
                  className="p-2 bg-white rounded-lg hover:bg-slate-100 transition-colors"
                >
                  {copiedId === item.id ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteId(item.id);
                  }}
                  className="p-2 bg-white rounded-lg hover:bg-red-50 transition-colors text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <ImageIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 mb-4">Nenhum arquivo enviado ainda.</p>
        </div>
      )}

      {/* Media Detail Dialog */}
      <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes do Arquivo</DialogTitle>
          </DialogHeader>
          {selectedMedia && (
            <div className="space-y-4">
              <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden">
                {selectedMedia.content_type?.startsWith('image/') ? (
                  <img
                    src={`/api/media/${selectedMedia.id}`}
                    alt={selectedMedia.alt_text || selectedMedia.original_filename}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-16 h-16 text-slate-300" />
                  </div>
                )}
              </div>
              <div className="space-y-2 text-sm">
                <p><strong>Nome:</strong> {selectedMedia.original_filename}</p>
                <p><strong>Tipo:</strong> {selectedMedia.content_type}</p>
                <p><strong>Tamanho:</strong> {formatFileSize(selectedMedia.size)}</p>
                <p><strong>Enviado em:</strong> {formatDateTime(selectedMedia.created_at)}</p>
              </div>
              <div>
                <label className="text-sm font-medium">URL do arquivo</label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={`/api/media/${selectedMedia.id}`}
                    readOnly
                    className="text-xs"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleCopyUrl(selectedMedia)}
                  >
                    {copiedId === selectedMedia.id ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteId(selectedMedia?.id)}
              className="text-red-500 hover:text-red-600"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir arquivo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O arquivo será permanentemente excluído.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminMedia;
